import { useState, useRef, useEffect, useCallback } from "react"
import { createPortal } from "react-dom";
import { Button, TextArea } from "react-aria-components";
import { HttpStatusCode } from "axios";
import { SensitiveDataApi } from "../../../modules/apis/SensitiveDataApi";
import { capitalizeFirstLetter } from "../../../helpers/StringHelper";
import { EntityNames } from "../../../enums/EntityNames";
import EyeIcon from "../../../assets/icons/icon-eye.svg";
import IconHide from "../../../assets/icons/icon-hide.svg";

// Global state to track which tooltip is open
let openTooltipId: string | null = null;
const tooltipCloseCallbacks: Map<string, () => void> = new Map();

interface INotesTooltip {
    item: {
        id: string | undefined;
        resultType: string;
        reference: string;
    }
}

export default function NotesTooltip({ item }: INotesTooltip) {
    const [notes, setNotes] = useState<string | undefined>(undefined);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [hasFetched, setHasFetched] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const enterTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [sensitiveDataApi] = useState(new SensitiveDataApi());
    const tooltipId = `notes-tooltip-${item.id}`;

    function getEntityName(resultType: string): string {
        switch (resultType) {
            case "Range": return EntityNames.ranges;
            case "Whole Case": return EntityNames.cases;
            case "Part Case": return EntityNames.specimens;
            default: return EntityNames.cases;
        }
    }

    async function getData() {
        if (hasFetched) return;
        
        setHasFetched(true);
        const timeout = setTimeout(() => setShowLoader(true), 200);
        
        const itemId = (item as any).id || (item as any).Id;
        const request = {
            id: typeof itemId === 'string' ? itemId : itemId?.toString() || '',
            entityName: capitalizeFirstLetter(getEntityName(item.resultType)),
            dataTarget: "notes"
        }
        
        const result = await sensitiveDataApi.getNotes(request);
        
        if (result?.status == HttpStatusCode.Ok) {
            setNotes(result.data?.data?.notes || "");
        }
        setShowLoader(false);
        clearTimeout(timeout);
    }

    const handleClose = useCallback(() => {
        setIsOpen(false);
        setPosition(null);
        if (openTooltipId === tooltipId) {
            openTooltipId = null;
        }
    }, [tooltipId]);

    const handleMouseEnter = () => {
        // Clear any pending leave timeout
        if (leaveTimeoutRef.current) {
            clearTimeout(leaveTimeoutRef.current);
            leaveTimeoutRef.current = null;
        }

        // Clear any existing enter timeout (in case mouse re-enters quickly)
        if (enterTimeoutRef.current) {
            clearTimeout(enterTimeoutRef.current);
        }

        // Debounce the tooltip opening to prevent spam when scrolling fast
        enterTimeoutRef.current = setTimeout(() => {
            // Close any other open tooltip first
            if (openTooltipId && openTooltipId !== tooltipId) {
                const closeCallback = tooltipCloseCallbacks.get(openTooltipId);
                if (closeCallback) {
                    closeCallback();
                }
                // Clear the global state immediately
                openTooltipId = null;
            }

            // Use setTimeout to ensure DOM is ready and previous tooltip is closed
            setTimeout(() => {
                const buttonElement = buttonRef.current;
                if (buttonElement) {
                    const rect = buttonElement.getBoundingClientRect();
                    const tooltipWidth = 400;
                    const tooltipHeight = 120; // Approximate height
                    const offsetAbove = 10;
                    
                    // Calculate position to the left and slightly above
                    let left = rect.left - tooltipWidth - 10; // 10px gap from the eye
                    let top = rect.top - offsetAbove;
                    
                    // Ensure tooltip stays within viewport
                    if (left < 10) {
                        left = 10; // Minimum left margin
                    }
                    if (top < 10) {
                        top = 10; // Minimum top margin
                    }
                    if (top + tooltipHeight > window.innerHeight - 10) {
                        top = window.innerHeight - tooltipHeight - 10; // Keep within bottom
                    }
                    
                    setPosition({ top, left });
                    setIsOpen(true);
                    openTooltipId = tooltipId;
                    getData();
                }
            }, 10); // Small delay to ensure previous tooltip closes
            enterTimeoutRef.current = null;
        }, 350); // 350ms debounce delay
    };

    const handleMouseLeave = useCallback(() => {
        // Clear any pending enter timeout (user left before debounce completed)
        if (enterTimeoutRef.current) {
            clearTimeout(enterTimeoutRef.current);
            enterTimeoutRef.current = null;
        }

        // Clear any existing leave timeout
        if (leaveTimeoutRef.current) {
            clearTimeout(leaveTimeoutRef.current);
        }
        // Close tooltip when mouse leaves after a small delay
        leaveTimeoutRef.current = setTimeout(() => {
            handleClose();
            leaveTimeoutRef.current = null;
        }, 100); // Small delay to allow moving to tooltip
    }, [handleClose]);

    const updatePosition = () => {
        const buttonElement = buttonRef.current;
        if (buttonElement && isOpen) {
            const rect = buttonElement.getBoundingClientRect();
            const tooltipWidth = 400;
            const tooltipHeight = 120; // Approximate height
            const offsetAbove = 10;
            
            // Calculate position to the left and slightly above
            let left = rect.left - tooltipWidth - 10; // 10px gap from the eye
            let top = rect.top - offsetAbove;
            
            // Ensure tooltip stays within viewport
            if (left < 10) {
                left = 10; // Minimum left margin
            }
            if (top < 10) {
                top = 10; // Minimum top margin
            }
            if (top + tooltipHeight > window.innerHeight - 10) {
                top = window.innerHeight - tooltipHeight - 10; // Keep within bottom
            }
            
            setPosition({ top, left });
        }
    };

    useEffect(() => {
        // Register close callback - use a ref to always get the latest state
        const closeFn = () => {
            setIsOpen(false);
            setPosition(null);
            if (openTooltipId === tooltipId) {
                openTooltipId = null;
            }
        };
        tooltipCloseCallbacks.set(tooltipId, closeFn);

        return () => {
            // Clean up timeouts on unmount
            if (enterTimeoutRef.current) {
                clearTimeout(enterTimeoutRef.current);
                enterTimeoutRef.current = null;
            }
            if (leaveTimeoutRef.current) {
                clearTimeout(leaveTimeoutRef.current);
                leaveTimeoutRef.current = null;
            }
            tooltipCloseCallbacks.delete(tooltipId);
            if (openTooltipId === tooltipId) {
                openTooltipId = null;
            }
        };
    }, [tooltipId]);

    useEffect(() => {
        if (isOpen) {
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);
            return () => {
                window.removeEventListener('scroll', updatePosition, true);
                window.removeEventListener('resize', updatePosition);
            };
        }
    }, [isOpen]);

    const popoverContent = isOpen && position ? (
        <div
            ref={tooltipRef}
            className="notes-tooltip"
            onMouseEnter={() => {
                // Clear any pending leave timeout when hovering over tooltip
                if (leaveTimeoutRef.current) {
                    clearTimeout(leaveTimeoutRef.current);
                    leaveTimeoutRef.current = null;
                }
            }}
            onMouseLeave={handleMouseLeave}
            style={{
                position: 'fixed',
                top: `${position.top}px`,
                left: `${position.left}px`,
                backgroundColor: 'var(--colours-primary-tint, #FAFBFF)',
                color: 'var(--colours-text-primary, #000)',
                width: '400px',
                padding: '12px',
                borderRadius: '8px',
                zIndex: 10000,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                border: '1px solid rgba(0, 0, 0, 0.1)'
            }}
        >
            <div className="notes-tooltip__content">
                <div className="notes-tooltip__header">
                    <h3 className="small" style={{ color: 'var(--colours-text-primary, #000)', margin: '0 0 4px 0' }}>Notes - {item.reference}</h3>
                </div>
                <div className="notes-tooltip__body">
                    {showLoader ? (
                        <div className="loaders loaders__panel"></div>
                    ) : notes !== undefined ? (
                        <TextArea 
                            value={notes || ''} 
                            readOnly
                            disabled
                            tabIndex={-1}
                            className="notes-tooltip__textarea"
                            data-testid="readOnlyNotes"
                        />
                    ) : (
                        <div style={{ color: 'var(--colours-text-secondary, #666)', padding: '4px' }}>Loading...</div>
                    )}
                </div>
            </div>
        </div>
    ) : null;

    return (
        <>
            <Button
                ref={buttonRef}
                className="button button--text-only button--with-icon notes-tooltip__button"
                onHoverStart={handleMouseEnter}
                onHoverEnd={handleMouseLeave}
                aria-label="View notes"
            >
                <img 
                    src={isOpen ? IconHide : EyeIcon} 
                    alt={isOpen ? "Hide notes" : "View notes"} 
                    className="notes-tooltip__icon"
                    style={{ pointerEvents: 'auto' }}
                />
            </Button>
            {popoverContent && createPortal(popoverContent, document.body)}
        </>
    )
}
